import { useCallback, type RefObject } from 'react';

/**
 * Serializes the DOM content of a contentEditable mention editor
 * into structured text with mention markers like `@[Name](user:id)`.
 *
 * Works alongside the parsing utilities in `shared/utils/mention-parser.ts`
 * which handle the reverse direction (structured text -> segments).
 */
export function useMentionSerializer(
  editorRef: RefObject<HTMLDivElement | null>
): () => string {
  const getStructuredText = useCallback((): string => {
    if (!editorRef.current) return '';

    let structuredText = '';

    editorRef.current.childNodes.forEach((childNode) => {
      if (
        childNode instanceof HTMLElement &&
        childNode.classList.contains('ghost-hint')
      )
        return;

      if (
        childNode instanceof HTMLElement &&
        childNode.classList.contains('mention')
      ) {
        const entityType = childNode.getAttribute('data-type') ?? 'user';
        const entityId = childNode.getAttribute('data-id') ?? '';
        const displayName = (childNode.textContent || '').replace(/^@/, '');
        structuredText += `@[${displayName}](${entityType}:${entityId})`;
      } else {
        structuredText += childNode.textContent || '';
      }
    });

    return structuredText;
  }, [editorRef]);

  return getStructuredText;
}
